export const APPEARANCE_PREFERENCES = ["system", "light", "dark"] as const;

export type AppearancePreference = (typeof APPEARANCE_PREFERENCES)[number];

export type ResolvedAppearance = "light" | "dark";

export const UI_APPEARANCE_STORAGE_KEY = "rakazo.uiAppearance";

/**
 * Semantic palette shared by web, Electron, and Expo. Names follow the shadcn
 * convention so the same slot means the same thing on every surface: a `border`
 * is always a border and never a fill.
 */
export type ColorTokens = {
  background: string;
  /** Modals: a step lighter than the page so they read as raised above it. */
  dialog: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  /** Fill and hairline where native Liquid Glass is unavailable (Android, iOS before 26). */
  glassFill: string;
  glassStroke: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  /** Your messages: the accent fill. */
  chatUser: string;
  /** White in both themes, on the accent fill. */
  chatUserForeground: string;
  muted: string;
  mutedForeground: string;
  /** Tertiary text: placeholders, list markers, quote bars, timestamps. */
  faintForeground: string;
  accent: string;
  accentForeground: string;
  /** Destructive text and outlines. */
  destructive: string;
  /** Solid destructive fill (Hang up, a filled Delete); its text is `destructiveForeground`. */
  destructiveFill: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarBorder: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  link: string;
  /** Backing behind a link's site icon. White in both themes so dark logos stay visible on any bubble. */
  faviconPlate: string;
  success: string;
  warning: string;
  statusWorking: string;
  /** Unread dot. The one blue in the inbox, in both themes. */
  unread: string;
  /** Working dot on a busy bot's avatar. The one green, in both themes. */
  workingDot: string;
  overlay: string;
  scrollbar: string;
  scrollbarHover: string;
};

export const darkTokens = {
  background: "#141414",
  dialog: "#1E1E1E",
  foreground: "#FFFFFF",
  card: "#202020",
  cardForeground: "#FFFFFF",
  popover: "#202020",
  popoverForeground: "#FFFFFF",
  glassFill: "#2C2C2E",
  glassStroke: "#FCFCFC26",
  primary: "#2C6CE8",
  primaryForeground: "#FFFFFF",
  secondary: "#7676803D",
  secondaryForeground: "#FFFFFF",
  chatUser: "#2C6CE8",
  chatUserForeground: "#FFFFFF",
  muted: "#202020",
  mutedForeground: "#EBEBF599",
  faintForeground: "#EBEBF573",
  accent: "#78788052",
  accentForeground: "#FFFFFF",
  destructive: "#FF9592",
  destructiveFill: "#E5484D",
  destructiveForeground: "#FFFFFF",
  border: "#FCFCFC14",
  input: "#54545899",
  ring: "#3B82F6",
  sidebar: "#1E1E1E",
  sidebarForeground: "#FFFFFF",
  sidebarBorder: "#FCFCFC14",
  sidebarAccent: "#2B2B2E",
  sidebarAccentForeground: "#FFFFFF",
  link: "#6AA6FF",
  faviconPlate: "#FFFFFF",
  success: "#3FA266",
  warning: "#DB941E",
  statusWorking: "#6AA6FF",
  unread: "#006CEB",
  workingDot: "#3FA266",
  overlay: "rgba(0, 0, 0, 0.28)",
  scrollbar: "#1E2026",
  scrollbarHover: "#2E313A",
} as const satisfies ColorTokens;

export const lightTokens = {
  background: "#FCFCFC",
  dialog: "#FFFFFF",
  foreground: "#000000",
  card: "#EFEFEF",
  cardForeground: "#000000",
  popover: "#FFFFFF",
  popoverForeground: "#000000",
  glassFill: "#FFFFFF",
  glassStroke: "#14141416",
  primary: "#2563EB",
  primaryForeground: "#FFFFFF",
  secondary: "#7676801F",
  secondaryForeground: "#000000",
  chatUser: "#2563EB",
  chatUserForeground: "#FFFFFF",
  muted: "#F2F2F1",
  mutedForeground: "#3C3C43B3",
  faintForeground: "#3C3C4394",
  accent: "#78788029",
  accentForeground: "#000000",
  destructive: "#CE2C31",
  destructiveFill: "#E5484D",
  destructiveForeground: "#FFFFFF",
  border: "#14141414",
  input: "#3C3C434A",
  ring: "#6C6C70",
  sidebar: "#F7F7F7",
  sidebarForeground: "#000000",
  sidebarBorder: "#14141414",
  sidebarAccent: "#FFFFFF",
  sidebarAccentForeground: "#000000",
  link: "#1D4ED8",
  faviconPlate: "#FFFFFF",
  success: "#007041",
  warning: "#885A07",
  statusWorking: "#1D4ED8",
  unread: "#006CEB",
  workingDot: "#3FA266",
  overlay: "rgba(0, 0, 0, 0.19)",
  scrollbar: "#C8C8C4",
  scrollbarHover: "#A8A8A4",
} as const satisfies ColorTokens;

/**
 * Web and Electron only, from the Nexamero UI design system: the primary fill's hover and disabled
 * states and its strong end (accent text on light grounds, and the far stop of the primary
 * gradient), the accent tint behind a selected segment, the glass layers (`tile` boxes inside a
 * panel, `hairSoft` for the faintest edge, `inkFaint` for placeholders only), inline `code` chips,
 * and the three static blobs behind the shell (`blobA` top left, `blobB` bottom, `blobC` top right).
 */
export type WebColorTokens = ColorTokens & {
  primaryHover: string;
  primaryDisabled: string;
  primaryStrong: string;
  primarySubtle: string;
  primarySubtleForeground: string;
  tile: string;
  hairSoft: string;
  inkFaint: string;
  code: string;
  blobA: string;
  blobB: string;
  blobC: string;
};

/**
 * Web and Electron values for the tokens they draw differently from the palettes above, which
 * follow the iOS system design for mobile. Mobile reads `darkTokens` / `lightTokens`; web reads
 * `tokens.css`, rendered from these merged palettes. Panels are glass over a blurred colour wash:
 * `sidebar`, `card`, `tile` and `muted` are translucent and sit on `background`; `popover` and
 * `dialog` are solid. One sky accent carries the active thing and the main action; your own
 * messages are a soft mint so they never compete with it. Text-coloured status tokens are
 * darkened from the measured design where it falls under AA on the ground.
 */
export const webDarkOverrides = {
  background: "#070C16",
  dialog: "#101827",
  foreground: "#EEF4FF",
  card: "#94B2DE1A",
  cardForeground: "#EEF4FF",
  popover: "#101827",
  popoverForeground: "#EEF4FF",
  primary: "#38BDF8",
  primaryHover: "#7DD3FC",
  primaryDisabled: "#38BDF859",
  primaryStrong: "#7DD3FC",
  primaryForeground: "#070C16",
  primarySubtle: "#38BDF833",
  primarySubtleForeground: "#7DD3FC",
  secondary: "#94B2DE1F",
  secondaryForeground: "#EEF4FF",
  chatUser: "#1A4540",
  chatUserForeground: "#E8F4F0",
  muted: "#94B2DE12",
  mutedForeground: "#8FA1BD",
  faintForeground: "#7E90AC",
  accent: "#94B2DE29",
  accentForeground: "#EEF4FF",
  destructive: "#F05252",
  destructiveFill: "#DC2626",
  border: "#94B2DE29",
  input: "#94B2DE47",
  ring: "#38BDF8",
  sidebar: "#94B2DE1A",
  sidebarForeground: "#EEF4FF",
  sidebarBorder: "#94B2DE29",
  sidebarAccent: "#94B2DE29",
  sidebarAccentForeground: "#EEF4FF",
  link: "#7DD3FC",
  success: "#34D399",
  warning: "#F59E0B",
  statusWorking: "#7DD3FC",
  unread: "#38BDF8",
  workingDot: "#34D399",
  overlay: "#040405B8",
  scrollbar: "#94B2DE47",
  scrollbarHover: "#94B2DE80",
  tile: "#94B2DE1A",
  hairSoft: "#94B2DE1A",
  inkFaint: "#6B7D99",
  code: "#94B2DE24",
  blobA: "#16324F",
  blobB: "#0F3D38",
  blobC: "#3D2A1C",
} as const satisfies Partial<WebColorTokens>;

export const webLightOverrides = {
  background: "#F4F7FB",
  foreground: "#0F172A",
  card: "#FFFFFFCC",
  cardForeground: "#0F172A",
  popoverForeground: "#0F172A",
  primary: "#0284C7",
  primaryHover: "#0369A1",
  primaryDisabled: "#0284C766",
  primaryStrong: "#0369A1",
  primaryForeground: "#FFFFFF",
  primarySubtle: "#0284C71F",
  primarySubtleForeground: "#0369A1",
  secondary: "#0F172A0F",
  secondaryForeground: "#0F172A",
  chatUser: "#D6ECE4",
  chatUserForeground: "#0F172A",
  muted: "#FFFFFF85",
  mutedForeground: "#607088",
  faintForeground: "#7C8BA1",
  // Hover on any surface, solid menus included; rows on glass panels raise to `sidebarAccent`.
  accent: "#0F172A0D",
  accentForeground: "#0F172A",
  destructive: "#B91C1C",
  destructiveFill: "#DC2626",
  border: "#0F172A24",
  input: "#0F172A38",
  ring: "#0284C7",
  sidebar: "#FFFFFFB8",
  sidebarForeground: "#0F172A",
  sidebarBorder: "#0F172A24",
  sidebarAccent: "#FFFFFFEB",
  sidebarAccentForeground: "#0F172A",
  link: "#0369A1",
  success: "#047857",
  warning: "#B45309",
  statusWorking: "#0369A1",
  unread: "#0284C7",
  workingDot: "#10B981",
  overlay: "#14141673",
  scrollbar: "#0F172A38",
  scrollbarHover: "#0F172A66",
  tile: "#FFFFFF8C",
  hairSoft: "#0F172A0D",
  inkFaint: "#94A3B8",
  code: "#0F172A0E",
  blobA: "#CFE7FF",
  blobB: "#D6F5EE",
  blobC: "#FFE2C7",
} as const satisfies Partial<WebColorTokens>;

/**
 * Web shadows and backdrop filters. `elevationPanel` lifts the side panels, `elevationRaised` a row
 * lifted off its panel, `elevationAccent` sits under primary-gradient pills only, `elevationBubble`
 * is the top highlight on a bot bubble. `backdropShell` is the side panels' backdrop filter and
 * `backdropChat` the chat panel's.
 */
export type WebEffectTokens = {
  elevationPanel: string;
  elevationRaised: string;
  elevationAccent: string;
  elevationBubble: string;
  backdropShell: string;
  backdropChat: string;
};

export const webLightEffects = {
  elevationPanel:
    "0 8px 24px -14px rgba(15, 23, 42, 0.22), inset 0 0 0 0.5px rgba(15, 23, 42, 0.05)",
  elevationRaised: "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 12px -4px rgba(15, 23, 42, 0.12)",
  elevationAccent: "0 8px 18px -10px rgba(2, 132, 199, 0.9)",
  elevationBubble: "inset 0 1px 0 rgba(255, 255, 255, 0.92)",
  backdropShell: "blur(38px) saturate(1.8)",
  backdropChat: "blur(22px) saturate(1.8)",
} as const satisfies WebEffectTokens;

export const webDarkEffects = {
  elevationPanel:
    "0 8px 24px -14px rgba(0, 0, 0, 0.6), inset 0 0 0 0.5px rgba(148, 178, 222, 0.06)",
  elevationRaised: "0 1px 2px rgba(0, 0, 0, 0.4), 0 4px 12px -4px rgba(0, 0, 0, 0.5)",
  elevationAccent: "0 8px 18px -10px rgba(56, 189, 248, 0.7)",
  elevationBubble: "inset 0 1px 0 rgba(148, 178, 222, 0.08)",
  backdropShell: "blur(38px) saturate(1.8)",
  backdropChat: "blur(22px) saturate(1.8)",
} as const satisfies WebEffectTokens;

export const webDarkTokens: WebColorTokens = { ...darkTokens, ...webDarkOverrides };
export const webLightTokens: WebColorTokens = { ...lightTokens, ...webLightOverrides };

/** Dark palette. Prefer `tokensForAppearance` when theme-aware. */
export const tokens = darkTokens;

export const RADIUS = "0.75rem";

/**
 * The design system's corner radii, in px: `sm` the bubble tail and inline code, `md` settings
 * groups and menus, `lg` rows, bubbles and tiles, `shell` the three panels, the composer and
 * dialogs. Every control is fully round.
 */
export const radii = { sm: 8, md: 12, lg: 20, shell: 28 } as const;

export const fontWeight = { regular: "400", medium: "500", semibold: "600" } as const;

/**
 * Native text styles on the system font, in points: iOS body text is 17/22. Titles use the
 * font's own line spacing, written out so they don't inherit a smaller body line height.
 */
export const typeScale = {
  title: { fontSize: 22, lineHeight: 26, fontWeight: fontWeight.semibold },
  headline: { fontSize: 19, lineHeight: 23, fontWeight: fontWeight.semibold },
  cardTitle: { fontSize: 17, lineHeight: 22, fontWeight: fontWeight.medium },
  body: { fontSize: 17, lineHeight: 22 },
  secondary: { fontSize: 15, lineHeight: 19 },
  caption: { fontSize: 14, lineHeight: 18 },
  footnote: { fontSize: 12, lineHeight: 16 },
} as const;

/**
 * Text grows with the iOS text size until body text reaches 23pt, the xxxLarge size. Larger
 * accessibility sizes keep that size, so message bubbles, rows and cards keep their layout.
 */
export const MAX_FONT_SCALE = 23 / typeScale.body.fontSize;

/** The ink of a drawn bot's eyes, the same on every body colour and in both themes. */
export const botEyeColor = "#101014";

/** The light that catches the shoulder of a drawn bot, the same on every body colour. */
export const botHighlightColor = "#FFFFFF";

export const botColors = [
  "#3EC5A8",
  "#F5A03C",
  "#6A6BF5",
  "#9B5CF6",
  "#3B82F6",
  "#F2622A",
  "#D9508A",
] as const;

export function isAppearancePreference(
  value: string | null | undefined,
): value is AppearancePreference {
  return value === "system" || value === "light" || value === "dark";
}

export function normalizeAppearancePreference(
  raw: string | null | undefined,
): AppearancePreference {
  return isAppearancePreference(raw) ? raw : "system";
}

export type ResolveAppearancePreferenceOptions = {
  stored?: string | null;
  storage?: Pick<Storage, "getItem"> | null;
};

function getLocalStorage(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

export function resolveAppearancePreference(
  options: ResolveAppearancePreferenceOptions = {},
): AppearancePreference {
  const stored =
    options.stored !== undefined
      ? options.stored
      : readStoredAppearance(options.storage ?? getLocalStorage());
  return normalizeAppearancePreference(stored);
}

export function persistAppearancePreference(
  preference: AppearancePreference,
  storage: Pick<Storage, "setItem"> | null = getLocalStorage(),
): void {
  if (!storage) return;
  try {
    storage.setItem(UI_APPEARANCE_STORAGE_KEY, preference);
  } catch {
    // Ignore quota / private-mode failures; in-memory preference still applies.
  }
}

export function resolveAppearance(
  preference: AppearancePreference,
  system: ResolvedAppearance = "dark",
): ResolvedAppearance {
  if (preference === "system") return system;
  return preference;
}

export function tokensForAppearance(appearance: ResolvedAppearance): ColorTokens {
  return appearance === "light" ? lightTokens : darkTokens;
}

/** The web and Electron palette; `tokensForAppearance` is the mobile one. */
export function webTokensForAppearance(appearance: ResolvedAppearance): WebColorTokens {
  return appearance === "light" ? webLightTokens : webDarkTokens;
}

function readStoredAppearance(storage: Pick<Storage, "getItem"> | null | undefined): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(UI_APPEARANCE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `cardForeground` -> `--card-foreground` */
export function cssVariableName(token: keyof WebColorTokens | keyof WebEffectTokens): string {
  return `--${token.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`;
}

/** The web effects for a theme. */
export function webEffectsForAppearance(appearance: ResolvedAppearance): WebEffectTokens {
  return appearance === "light" ? webLightEffects : webDarkEffects;
}

function renderBlock(selector: string, colorScheme: ResolvedAppearance) {
  const palette = webTokensForAppearance(colorScheme);
  const effects = webEffectsForAppearance(colorScheme);
  const lines = [
    ...(Object.keys(palette) as (keyof WebColorTokens)[]).map(
      (token) => `  ${cssVariableName(token)}: ${palette[token].toLowerCase()};`,
    ),
    ...(Object.keys(effects) as (keyof WebEffectTokens)[]).map((token) => {
      const line = `  ${cssVariableName(token)}: ${effects[token]};`;
      // Long shadows wrap the way the formatter writes them.
      return line.length > 100 ? `  ${cssVariableName(token)}:\n    ${effects[token]};` : line;
    }),
    ...(Object.keys(radii) as (keyof typeof radii)[]).map(
      (name) => `  --corner-${name}: ${radii[name]}px;`,
    ),
  ];
  return `${selector} {\n  color-scheme: ${colorScheme};\n${lines.join("\n")}\n  --radius: ${RADIUS};\n}`;
}

/** The CSS in `tokens.css` (web and Electron). Generated from the web palettes so both stay in sync. */
export function renderTokensCss(): string {
  return `${[
    "/* Generated by `pnpm --filter @rakazo/ui-tokens generate`. Edit src/index.ts instead. */",
    renderBlock(':root,\n[data-theme="dark"]', "dark"),
    renderBlock('[data-theme="light"]', "light"),
  ].join("\n\n")}\n`;
}
